import React, { useState, useRef, useEffect } from 'react';
import {
  Route,
  Truck,
  Navigation,
  Activity,
  Eye,
  MapPin,
  Clock,
  Fuel,
  AlertTriangle,
  CheckCircle,
  Settings
} from 'lucide-react';

const RoutesAndMaps = () => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [currentMarkers, setCurrentMarkers] = useState([]);
  const [currentPolylines, setCurrentPolylines] = useState([]);

  // Datos simulados de camiones con rutas múltiples
  const [trucks] = useState([
    {
      id: 1,
      plateNumber: "ABC-123",
      model: "Mercedes Actros",
      driver: "Juan Pérez",
      status: "en_ruta",
      capacity: "40 tons",
      fuel: 85,
      color: "#3B82F6",
      currentLocation: { lat: 40.4168, lng: -3.7038 }, // Madrid
      route: {
        name: "Madrid - Valencia - Barcelona",
        stops: [
          { 
            name: "Madrid (Origen)", 
            coords: { lat: 40.4168, lng: -3.7038 },
            type: "origin",
            completed: true,
            estimatedTime: "08:00"
          },
          { 
            name: "Guadalajara", 
            coords: { lat: 40.6319, lng: -3.1601 },
            type: "waypoint",
            completed: true,
            estimatedTime: "09:15"
          },
          { 
            name: "Cuenca", 
            coords: { lat: 40.0703, lng: -2.1374 },
            type: "waypoint",
            completed: false,
            estimatedTime: "11:30"
          },
          { 
            name: "Valencia", 
            coords: { lat: 39.4699, lng: -0.3763 },
            type: "stop",
            completed: false,
            estimatedTime: "14:00"
          },
          { 
            name: "Castellón", 
            coords: { lat: 39.9864, lng: -0.0513 },
            type: "waypoint",
            completed: false,
            estimatedTime: "15:45"
          },
          { 
            name: "Barcelona (Destino)", 
            coords: { lat: 41.3851, lng: 2.1734 },
            type: "destination",
            completed: false,
            estimatedTime: "18:30"
          }
        ]
      },
      progress: 35,
      lastUpdate: new Date(),
      maintenance: "Próximo: 15/09/2024"
    },
    {
      id: 2,
      plateNumber: "JKL-012",
      model: "Man TGX",
      driver: "Ana Martínez",
      status: "en_ruta",
      capacity: "38 tons",
      fuel: 67,
      color: "#10B981",
      currentLocation: { lat: 43.2627, lng: -2.9253 }, // Bilbao
      route: {
        name: "Bilbao - Burgos - Madrid",
        stops: [
          { 
            name: "Bilbao (Origen)", 
            coords: { lat: 43.2627, lng: -2.9253 },
            type: "origin",
            completed: true,
            estimatedTime: "06:00"
          },
          { 
            name: "Vitoria", 
            coords: { lat: 42.8467, lng: -2.6716 },
            type: "waypoint",
            completed: true,
            estimatedTime: "07:00"
          },
          { 
            name: "Miranda de Ebro", 
            coords: { lat: 42.6863, lng: -2.9465 },
            type: "waypoint",
            completed: false,
            estimatedTime: "08:15"
          },
          { 
            name: "Burgos", 
            coords: { lat: 42.3440, lng: -3.6969 },
            type: "stop",
            completed: false,
            estimatedTime: "10:00"
          },
          { 
            name: "Aranda de Duero", 
            coords: { lat: 41.6729, lng: -3.6881 },
            type: "waypoint",
            completed: false,
            estimatedTime: "11:30"
          },
          { 
            name: "Madrid (Destino)", 
            coords: { lat: 40.4168, lng: -3.7038 },
            type: "destination",
            completed: false,
            estimatedTime: "14:00"
          }
        ]
      },
      progress: 45,
      lastUpdate: new Date(),
      maintenance: "Próximo: 10/10/2024"
    },
    {
      id: 3,
      plateNumber: "XYZ-789",
      model: "Volvo FH",
      driver: "Carlos López",
      status: "disponible",
      capacity: "44 tons",
      fuel: 92,
      color: "#F59E0B",
      currentLocation: { lat: 37.3891, lng: -5.9845 }, // Sevilla
      route: {
        name: "Sevilla - Córdoba - Toledo",
        stops: [
          { 
            name: "Sevilla (Origen)", 
            coords: { lat: 37.3891, lng: -5.9845 },
            type: "origin",
            completed: false,
            estimatedTime: "09:00"
          },
          { 
            name: "Córdoba", 
            coords: { lat: 37.8882, lng: -4.7794 },
            type: "stop",
            completed: false,
            estimatedTime: "11:00"
          },
          { 
            name: "Ciudad Real", 
            coords: { lat: 38.9848, lng: -3.9274 },
            type: "waypoint",
            completed: false,
            estimatedTime: "13:30"
          },
          { 
            name: "Toledo (Destino)", 
            coords: { lat: 39.8628, lng: -4.0273 },
            type: "destination",
            completed: false,
            estimatedTime: "15:00"
          }
        ]
      },
      progress: 0,
      lastUpdate: new Date(),
      maintenance: "Próximo: 20/09/2024"
    }
  ]);

  // Función para generar puntos intermedios realistas entre dos coordenadas
  const generateRealisticRoute = (start, end, curveFactor = 0.3) => {
    const steps = 50;
    const points = [];
    
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      
      // Interpolación lineal básica
      const lat = start.lat + (end.lat - start.lat) * t;
      const lng = start.lng + (end.lng - start.lng) * t;
      
      // Agregar variación para simular carreteras reales
      const variation = Math.sin(t * Math.PI * 3) * curveFactor;
      const perpLat = -(end.lng - start.lng) * variation * 0.01;
      const perpLng = (end.lat - start.lat) * variation * 0.01;
      
      points.push({
        lat: lat + perpLat,
        lng: lng + perpLng
      });
    }
    
    return points;
  };

  // Configuración del mapa de Google Maps
  useEffect(() => {
    if (!mapRef.current) return;

    const initMap = () => {
      if (!window.google || !mapRef.current) return;

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        zoom: 6,
        center: { lat: 40.4168, lng: -3.7038 },
        mapTypeId: 'roadmap',
        styles: [
          {
            featureType: 'all',
            elementType: 'geometry.fill',
            stylers: [{ color: '#f5f5f5' }]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#e9e9e9' }, { lightness: 17 }]
          }
        ]
      });

      setMap(mapInstance);

      // Mostrar todos los camiones inicialmente
      showAllTrucks(mapInstance);
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAEJCPsqyTflPDiV8pUJjVG2ehJds7Eb3g&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Función para mostrar todos los camiones
  const showAllTrucks = (mapInstance) => {
    clearMapElements();
    
    const markers = [];
    
    trucks.forEach(truck => {
      const marker = new window.google.maps.Marker({
        position: truck.currentLocation,
        map: mapInstance,
        title: `${truck.plateNumber} - ${truck.driver}`,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" fill="${truck.color}" stroke="white" stroke-width="2"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(16, 16),
          anchor: new window.google.maps.Point(8, 8)
        }
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h4 style="margin: 0 0 8px 0; color: #1a1a1a;">${truck.plateNumber}</h4>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #4a4a4a;">
              <strong>Conductor:</strong> ${truck.driver}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #4a4a4a;">
              <strong>Modelo:</strong> ${truck.model}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #4a4a4a;">
              <strong>Combustible:</strong> ${truck.fuel}%
            </p>
            <p style="margin: 0; font-size: 12px; color: #4a4a4a;">
              <strong>Estado:</strong> ${truck.status}
            </p>
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(mapInstance, marker);
      });

      markers.push(marker);
    });
    
    setCurrentMarkers(markers);
  };

  // Función para limpiar elementos del mapa
  const clearMapElements = () => {
    currentMarkers.forEach(marker => marker.setMap(null));
    currentPolylines.forEach(polyline => polyline.setMap(null));
    setCurrentMarkers([]);
    setCurrentPolylines([]);
  };

  // Función para mostrar ruta de un camión específico
  const showTruckRoute = (truck) => {
    if (!map || !truck.route) return;
    
    setSelectedTruck(truck);
    clearMapElements();

    const markers = [];
    const polylines = [];
    
    // Crear marcadores para cada parada
    truck.route.stops.forEach((stop, index) => {
      let iconConfig;
      
      switch (stop.type) {
        case 'origin':
          iconConfig = {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" fill="#10B981" stroke="white" stroke-width="2"/>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(20, 20),
            anchor: new window.google.maps.Point(10, 10)
          };
          break;
        case 'destination':
          iconConfig = {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="white" stroke-width="2"/>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(20, 20),
            anchor: new window.google.maps.Point(10, 10)
          };
          break;
        case 'stop':
          iconConfig = {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" fill="${truck.color}" stroke="white" stroke-width="2"/>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(24, 24),
            anchor: new window.google.maps.Point(12, 12)
          };
          break;
        default: // waypoint
          iconConfig = {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="6" cy="6" r="5" fill="${stop.completed ? '#10B981' : '#9CA3AF'}" stroke="white" stroke-width="1"/>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(12, 12),
            anchor: new window.google.maps.Point(6, 6)
          };
      }

      const marker = new window.google.maps.Marker({
        position: stop.coords,
        map: map,
        title: stop.name,
        icon: iconConfig
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h4 style="margin: 0 0 8px 0; color: #1a1a1a;">${stop.name}</h4>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #4a4a4a;">
              <strong>Tipo:</strong> ${stop.type}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #4a4a4a;">
              <strong>Hora estimada:</strong> ${stop.estimatedTime}
            </p>
            <p style="margin: 0; font-size: 12px;">
              <strong>Estado:</strong> 
              <span style="color: ${stop.completed ? '#10B981' : '#F59E0B'};">
                ${stop.completed ? 'Completado' : 'Pendiente'}
              </span>
            </p>
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      markers.push(marker);

      // Crear líneas entre paradas
      if (index < truck.route.stops.length - 1) {
        const nextStop = truck.route.stops[index + 1];
        const routePoints = generateRealisticRoute(stop.coords, nextStop.coords);
        
        const polyline = new window.google.maps.Polyline({
          path: routePoints,
          geodesic: true,
          strokeColor: stop.completed ? '#10B981' : truck.color,
          strokeOpacity: stop.completed ? 0.8 : 1.0,
          strokeWeight: stop.completed ? 3 : 4,
          icons: stop.completed ? [] : [{
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              scale: 3,
              strokeColor: truck.color
            },
            repeat: '20px'
          }]
        });

        polyline.setMap(map);
        polylines.push(polyline);
      }
    });

    // Marcador especial para la posición actual del camión
    const currentPositionMarker = new window.google.maps.Marker({
      position: truck.currentLocation,
      map: map,
      title: `${truck.plateNumber} - Posición Actual`,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="24" height="32" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z" fill="${truck.color}"/>
            <circle cx="12" cy="12" r="8" fill="white"/>
            <circle cx="12" cy="12" r="6" fill="${truck.color}"/>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(24, 32),
        anchor: new window.google.maps.Point(12, 32)
      },
      zIndex: 1000
    });

    markers.push(currentPositionMarker);

    setCurrentMarkers(markers);
    setCurrentPolylines(polylines);

    // Ajustar la vista del mapa para mostrar toda la ruta
    const bounds = new window.google.maps.LatLngBounds();
    truck.route.stops.forEach(stop => {
      bounds.extend(stop.coords);
    });
    bounds.extend(truck.currentLocation);
    map.fitBounds(bounds);
  };

  // Función para volver a mostrar todos los camiones
  const showAllTrucksView = () => {
    if (!map) return;
    
    setSelectedTruck(null);
    showAllTrucks(map);
    
    // Centrar el mapa en España
    map.setCenter({ lat: 40.4168, lng: -3.7038 });
    map.setZoom(6);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_ruta': return '#3B82F6'
      case 'disponible': return '#10B981'
      case 'mantenimiento': return '#F59E0B'
      default: return '#6B7280'
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'en_ruta': return 'En Ruta'
      case 'disponible': return 'Disponible'
      case 'mantenimiento': return 'Mantenimiento'
      default: return 'Desconocido'
    }
  };

  return (
    <div className="routes-and-maps">
      <header className="content-header">
        <div className="header-info">
          <h2>Rutas y Mapas en Tiempo Real</h2>
          <p>Seguimiento GPS y optimización de rutas</p>
        </div>
        <div className="header-metrics">
          <div className="metric-item">
            <div className="metric-icon active">
              <Route />
            </div>
            <div>
              <strong>{trucks.filter(t => t.status === 'en_ruta').length}</strong>
              <span>Rutas Activas</span>
            </div>
          </div>
          <div className="metric-item">
            <div className="metric-icon tracking">
              <Navigation />
            </div>
            <div>
              <strong>GPS</strong>
              <span>Activo</span>
            </div>
          </div>
        </div>
      </header>

      <div className="routes-content">
        <div className="routes-controls glass-panel">
          <div className="controls-section">
            <div className="section-header">
              <h3>Control de Rutas</h3>
              {selectedTruck && (
                <button 
                  className="view-all-btn"
                  onClick={showAllTrucksView}
                >
                  <Eye size={14} />
                  Ver Todos
                </button>
              )}
            </div>
            
            <div className="truck-list">
              {trucks.map(truck => (
                <div 
                  key={truck.id}
                  className={`truck-item ${selectedTruck?.id === truck.id ? 'selected' : ''}`}
                  onClick={() => showTruckRoute(truck)}
                >
                  <div className="truck-header">
                    <div className="truck-info">
                      <Truck size={16} />
                      <span className="truck-plate">{truck.plateNumber}</span>
                      <div 
                        className="truck-color-indicator" 
                        style={{ backgroundColor: truck.color }}
                      ></div>
                    </div>
                    <div className="truck-status">
                      <div 
                        className="status-dot" 
                        style={{ backgroundColor: getStatusColor(truck.status) }}
                      ></div>
                      <span className="status-text">{getStatusText(truck.status)}</span>
                    </div>
                  </div>
                  
                  <div className="truck-details">
                    <div className="truck-route">
                      <MapPin size={12} />
                      <span>{truck.route.name}</span>
                    </div>
                    <div className="truck-driver">
                      <span>Conductor: {truck.driver}</span>
                    </div>
                    <div className="truck-fuel">
                      <Fuel size={12} />
                      <span>{truck.fuel}% combustible</span>
                    </div>
                  </div>
                  
                  {truck.status === 'en_ruta' && (
                    <div className="truck-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ 
                            width: `${truck.progress}%`,
                            backgroundColor: truck.color
                          }}
                        ></div>
                      </div>
                      <span className="progress-text">{truck.progress}% completado</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Información de la ruta seleccionada */}
          {selectedTruck && (
            <div className="selected-route-section">
              <h4>Detalles de la Ruta</h4>
              <div className="route-stops">
                {selectedTruck.route.stops.map((stop, index) => (
                  <div key={index} className={`stop-item ${stop.completed ? 'completed' : ''}`}>
                    <div className="stop-indicator">
                      <div 
                        className={`stop-dot ${stop.type}`}
                        style={{
                          backgroundColor: stop.completed ? '#10B981' : 
                            stop.type === 'origin' ? '#10B981' :
                            stop.type === 'destination' ? '#EF4444' :
                            stop.type === 'stop' ? selectedTruck.color :
                            '#9CA3AF'
                        }}
                      ></div>
                      {index < selectedTruck.route.stops.length - 1 && (
                        <div className={`stop-line ${stop.completed ? 'completed' : ''}`}></div>
                      )}
                    </div>
                    <div className="stop-content">
                      <div className="stop-name">{stop.name}</div>
                      <div className="stop-time">
                        <Clock size={10} />
                        {stop.estimatedTime}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Estado del sistema */}
          <div className="system-status-section">
            <h4>Estado del Sistema</h4>
            <div className="status-items">
              <div className="status-item good">
                <CheckCircle size={16} />
                <span>GPS Conectado</span>
              </div>
              <div className="status-item good">
                <Activity size={16} />
                <span>Tiempo Real</span>
              </div>
              <div className="status-item warning">
                <AlertTriangle size={16} />
                <span>1 Alerta de Tráfico</span>
              </div>
            </div>
          </div>
        </div>

        <div className="map-container glass-panel">
          <div className="map-header">
            <h3>
              {selectedTruck ? 
                `Ruta de ${selectedTruck.plateNumber}` : 
                'Mapa de Rutas en Tiempo Real'
              }
            </h3>
            <div className="map-actions">
              <button className="map-action-btn">
                <Navigation size={16} />
                Centrar
              </button>
              <button className="map-action-btn">
                <Activity size={16} />
                Tiempo Real
              </button>
              <button className="map-action-btn">
                <Settings size={16} />
                Opciones
              </button>
            </div>
          </div>
          <div ref={mapRef} className="google-map"></div>
          
          {/* Leyenda del mapa */}
          <div className="map-legend-overlay">
            <div className="legend-title">Leyenda</div>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-marker origin"></div>
                <span>Origen</span>
              </div>
              <div className="legend-item">
                <div className="legend-marker destination"></div>
                <span>Destino</span>
              </div>
              <div className="legend-item">
                <div className="legend-marker stop"></div>
                <span>Parada</span>
              </div>
              <div className="legend-item">
                <div className="legend-marker waypoint"></div>
                <span>Punto de paso</span>
              </div>
              <div className="legend-item">
                <div className="legend-marker current"></div>
                <span>Posición actual</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Panel de estadísticas de rutas */}
      <div className="route-statistics glass-panel">
        <div className="statistics-header">
          <h3>Estadísticas de Rutas</h3>
        </div>
        <div className="statistics-grid">
          <div className="stat-card">
            <div className="stat-icon primary">
              <Route size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">1,247 km</div>
              <div className="stat-label">Total Recorrido Hoy</div>
              <div className="stat-trend positive">+12% vs ayer</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon success">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">94.2%</div>
              <div className="stat-label">Puntualidad</div>
              <div className="stat-trend positive">+2.1% vs semana</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon warning">
              <Fuel size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{Math.round(trucks.reduce((acc, t) => acc + t.fuel, 0) / trucks.length)}%</div>
              <div className="stat-label">Combustible Promedio</div>
              <div className="stat-trend neutral">Estable</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon info">
              <Activity size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">2.1 hrs</div>
              <div className="stat-label">Tiempo Promedio</div>
              <div className="stat-trend positive">-15 min optimizado</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .routes-and-maps {
          padding: 24px;
          background: #f9fafb;
          min-height: 100vh;
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .header-info h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .header-info p {
          color: #4a4a4a;
          margin: 0;
        }

        .header-metrics {
          display: flex;
          gap: 16px;
        }

        .metric-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .metric-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-icon.active {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .metric-icon.tracking {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .metric-item strong {
          display: block;
          font-size: 18px;
          font-weight: 700;
          color: #1a1a1a;
        }

        .metric-item span {
          font-size: 12px;
          color: #4a4a4a;
        }

        .routes-content {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 24px;
          margin-bottom: 24px;
        }

        .glass-panel {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: 1px solid #e5e7eb;
        }

        .routes-controls {
          padding: 20px;
          height: fit-content;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .view-all-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #f9fafb;
          color: #4a4a4a;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .view-all-btn:hover {
          background: white;
          color: #3b82f6;
          border-color: #3b82f6;
        }

        .truck-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .truck-item {
          padding: 16px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          background: #f9fafb;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .truck-item:hover {
          background: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .truck-item.selected {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
        }

        .truck-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .truck-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .truck-plate {
          font-weight: 600;
          font-size: 14px;
          color: #1a1a1a;
        }

        .truck-color-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
        }

        .truck-status {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .status-text {
          font-size: 12px;
          color: #4a4a4a;
        }

        .truck-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 12px;
        }

        .truck-route, .truck-driver, .truck-fuel {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #4a4a4a;
        }

        .truck-progress {
          margin-top: 8px;
        }

        .progress-bar {
          height: 4px;
          background: #e2e8f0;
          border-radius: 2px;
          overflow: hidden;
          margin-bottom: 4px;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 11px;
          color: #4a4a4a;
        }

        .selected-route-section {
          margin-bottom: 24px;
        }

        .selected-route-section h4 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 16px;
          color: #1a1a1a;
        }

        .route-stops {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .stop-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          position: relative;
        }

        .stop-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .stop-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
          z-index: 2;
          position: relative;
        }

        .stop-dot.origin {
          width: 14px;
          height: 14px;
        }

        .stop-dot.destination {
          width: 14px;
          height: 14px;
        }

        .stop-dot.stop {
          width: 16px;
          height: 16px;
        }

        .stop-line {
          width: 2px;
          height: 24px;
          background: #e5e7eb;
          margin-top: 4px;
        }

        .stop-line.completed {
          background: #10b981;
        }

        .stop-content {
          flex: 1;
          padding-bottom: 16px;
        }

        .stop-name {
          font-size: 13px;
          font-weight: 500;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .stop-time {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #4a4a4a;
        }

        .system-status-section h4 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 16px;
          color: #1a1a1a;
        }

        .status-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          padding: 8px 12px;
          border-radius: 6px;
        }

        .status-item.good {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .status-item.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .map-container {
          overflow: hidden;
          position: relative;
        }

        .map-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .map-header h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }

        .map-actions {
          display: flex;
          gap: 8px;
        }

        .map-action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #f9fafb;
          color: #4a4a4a;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .map-action-btn:hover {
          background: white;
          color: #3b82f6;
          border-color: #3b82f6;
        }

        .google-map {
          width: 100%;
          height: 687px;
          border: none;
        }

        .map-legend-overlay {
          position: absolute;
          bottom: 20px;
          left: 20px;
          background: white;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .legend-title {
          font-size: 12px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .legend-items {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          color: #4a4a4a;
        }

        .legend-marker {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 1px solid white;
          box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
        }

        .legend-marker.origin {
          background: #10B981;
        }

        .legend-marker.destination {
          background: #EF4444;
        }

        .legend-marker.stop {
          background: #3B82F6;
        }

        .legend-marker.waypoint {
          background: #9CA3AF;
        }

        .legend-marker.current {
          background: #F59E0B;
          border-radius: 0;
          width: 8px;
          height: 12px;
        }

        /* Route Statistics */
        .route-statistics {
          padding: 24px;
        }

        .statistics-header {
          margin-bottom: 24px;
        }

        .statistics-header h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          color: #1a1a1a;
        }

        .statistics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          transform: translateY(-1px);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon.primary {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .stat-icon.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .stat-icon.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .stat-icon.info {
          background: rgba(124, 58, 237, 0.1);
          color: #7c3aed;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #4a4a4a;
          margin-bottom: 4px;
        }

        .stat-trend {
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .stat-trend.positive {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .stat-trend.neutral {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .routes-content {
            grid-template-columns: 1fr;
          }
          
          .routes-controls {
            order: 2;
          }
          
          .map-container {
            order: 1;
          }
        }

        @media (max-width: 768px) {
          .content-header {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .header-metrics {
            justify-content: space-between;
          }

          .statistics-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .google-map {
            height: 400px;
          }
        }

        @media (max-width: 576px) {
          .routes-and-maps {
            padding: 16px;
          }
          
          .statistics-grid {
            grid-template-columns: 1fr;
          }
          
          .routes-content {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default RoutesAndMaps;
      